import type { ApiError } from "./callApi";
import { getEnv } from "./env";
import { getRequestAuthToken } from "./requestAuth";

/** Progress for an in-flight upload — mirrors XHR's ProgressEvent. */
export type UploadProgress = {
  /** Bytes sent so far. */
  loaded: number;
  /** Total bytes to send (0 if the browser can't compute it). */
  total: number;
  /** 0–100, or null while the total length is unknown. */
  percent: number | null;
};

export type UploadWithProgressOptions = {
  /** Fired as bytes are pushed to the server (browser-side measurement). */
  onProgress?: (progress: UploadProgress) => void;
  /** Abort signal — aborting rejects the returned promise. */
  signal?: AbortSignal;
};

/**
 * POST a multipart body to the backend with real upload-progress reporting.
 *
 * `callApi` uses `fetch()`, which has no upload-progress event, so file uploads
 * that need a progress bar go through this XHR-based path instead. Progress is
 * measured entirely in the browser (bytes flushed to the socket) — the backend
 * endpoint is unchanged. Auth uses `getRequestAuthToken` (the unified token
 * store) so it works for both MSAL staff and external email-OTP users.
 */
export async function uploadWithProgress<T = unknown>(
  apiUri: string,
  formData: FormData,
  { onProgress, signal }: UploadWithProgressOptions = {}
): Promise<T> {
  // Normalise the base URL: tolerate VITE_BACKEND_URL with or without a
  // trailing slash (Front Door origins are configured without one).
  const baseAPIUrl = getEnv("VITE_BACKEND_URL").replace(/\/+$/, "");
  const apiUrl = `${baseAPIUrl}/api/v1/${apiUri}`;

  // Auth for BOTH staff (MSAL) and external OTP users: read the unified
  // in-memory token store the generated api client uses. The old MSAL-only path
  // threw for external doctors/referrers (no MSAL account), so their uploads
  // went out unauthenticated → 403 "Not authenticated" → the UI's misleading
  // "Transcription service is unavailable".
  const accessToken = await getRequestAuthToken();

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl);
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    // Do NOT set Content-Type — the browser sets the multipart boundary.

    xhr.upload.onprogress = (event) => {
      onProgress?.({
        loaded: event.loaded,
        total: event.total,
        percent: event.lengthComputable
          ? Math.round((event.loaded / event.total) * 100)
          : null,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : (null as T));
        } catch {
          reject(new Error("Failed to parse upload response"));
        }
        return;
      }
      // Surface FastAPI's `detail` where present, matching callApi's shape.
      let detail: string | undefined;
      try {
        const body = JSON.parse(xhr.responseText);
        const raw = body?.detail;
        detail =
          typeof raw === "string"
            ? raw
            : raw != null
              ? JSON.stringify(raw)
              : undefined;
      } catch {
        // non-JSON error body
      }
      const error = new Error(
        detail
          ? `Upload failed with status ${xhr.status}: ${detail}`
          : `Upload failed with status ${xhr.status}: ${xhr.statusText}`
      ) as ApiError;
      error.status = xhr.status;
      error.detail = detail;
      reject(error);
    };

    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(formData);
  });
}
